import type { FunctionComponent } from 'react';
import cx from 'classnames';
import SubnavItem, { type SubnavItemProps } from './SubnavItem';
import SubnavItemWithCount, { type SubnavItemWithCountProps } from './SubnavItemWithCount';
import SubnavPaidFeatureItem, { type SubnavPaidFeatureItemProps } from './SubnavPaidFeatureItem';
import SubnavDropdown, { type SubnavDropdownComponent } from './SubnavDropdown';
import SubnavContextProvider, { useSubnavContextProvider } from './context';
import styles from './Subnav.module.less';

type SubnavProps = {
  activeKey?: string;
  variant?: 'small' | 'regular';
};

type SubnavType = FunctionComponent<SubnavProps> & {
  Item: FunctionComponent<SubnavItemProps>;
  ItemWithCount: FunctionComponent<SubnavItemWithCountProps>;
  PaidFeatureItem: FunctionComponent<SubnavPaidFeatureItemProps>;
  Dropdown: SubnavDropdownComponent;
};

/**
 * A fully generic Subnav compound component. Allows you to put any valid element into the navigation items
 * to keep rendering control in the hands of the parent component.
 *
 * @example
 * <Subnav activeKey="google">
 *   <Subnav.Item to="https://google.com" navKey="google">
 *     Google
 *   </Subnav.Item>
 *   <Subnav.Item to="https://bing.com" navKey="bing" openInNewTab>
 *     Bing
 *   </Subnav.Item>
 * </Subnav>
 */
const Subnav: SubnavType = ({ children, activeKey, variant = 'regular' }) => {
  const contextValue = useSubnavContextProvider(activeKey);
  return (
    <SubnavContextProvider value={contextValue}>
      <nav className={cx(styles.subnav, {
        [styles.subnavSmall]: variant === 'small',
        [styles.subnavRegular]: variant === 'regular',
      })}>
        <div className={styles.subnavContent}>
          {children}
        </div>
      </nav>
    </SubnavContextProvider>
  );
};

Subnav.Item = SubnavItem;
Subnav.ItemWithCount = SubnavItemWithCount;
Subnav.PaidFeatureItem = SubnavPaidFeatureItem;
Subnav.Dropdown = SubnavDropdown;

export default Subnav;
